import {createApi, FetchBaseQueryError} from '@reduxjs/toolkit/query/react'
import {baseQueryWithReAuth} from "@/store/features/baseRtkQuery";

export interface ExternalAgentCallRequest {
    agentId: string;
    message: string;
    projectId: string | null;
    userId: string;
}

export interface ExternalAgentCallResponse {
    success: boolean;
    message: string;
    messageId: number;
    inputTokens: number;
    outputTokens: number;
    chatId: string;
    agentName: string;
    agentVersion: string;
    projectId: string;
}

// Validation function for request
const validateRequest = (request: ExternalAgentCallRequest): { isValid: boolean; error?: string } => {
    if (!request.agentId || typeof request.agentId !== 'string' || request.agentId.trim() === '') {
        return { isValid: false, error: 'agentId is required and must be a non-empty string' };
    }
    if (!request.message || typeof request.message !== 'string' || request.message.trim() === '') {
        return { isValid: false, error: 'message is required and must be a non-empty string' };
    }
    if (!request.userId || typeof request.userId !== 'string' || request.userId.trim() === '') {
        return { isValid: false, error: 'userId is required and must be a non-empty string' };
    }
    if (request.projectId !== null && (typeof request.projectId !== 'string' || request.projectId.trim() === '')) {
        return { isValid: false, error: 'projectId must be null or a non-empty string' };
    }
    return { isValid: true };
};

// Validation function for response
const validateResponse = (response: unknown): response is ExternalAgentCallResponse => {
    if (!response || typeof response !== 'object') {
        return false;
    }
    const res = response as Record<string, unknown>;
    return (
        typeof res.success === 'boolean' &&
        typeof res.message === 'string' &&
        typeof res.messageId === 'number' &&
        typeof res.inputTokens === 'number' &&
        typeof res.outputTokens === 'number' &&
        typeof res.chatId === 'string' &&
        typeof res.agentName === 'string' &&
        typeof res.agentVersion === 'string' &&
        typeof res.projectId === 'string'
    );
};

export const externalAgentApiSlice = createApi({
    reducerPath: 'externalAgentApi',
    baseQuery: baseQueryWithReAuth,
    tagTypes: [],
    endpoints: (builder) => ({
        callExternalAgent: builder.mutation<ExternalAgentCallResponse, ExternalAgentCallRequest>({
            query: (body) => {
                // Validate request before sending
                const validation = validateRequest(body);
                if (!validation.isValid) {
                    throw new Error(validation.error || 'Invalid request parameters');
                }

                return {
                    url: 'ExternalAgent/call',
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body,
                };
            },
            transformResponse: (response: unknown): ExternalAgentCallResponse => {
                // Validate response structure
                if (!validateResponse(response)) {
                    console.error('Invalid response structure from ExternalAgent/call:', response);
                    throw new Error('Invalid response structure from API');
                }
                return response;
            },
            transformErrorResponse: (response: FetchBaseQueryError) => {
                let errorMessage = 'An unknown error occurred';
                let status: number | undefined;
                let data: unknown;

                if ('status' in response) {
                    if (typeof response.status === 'number') {
                        status = response.status;
                        data = response.data;
                        errorMessage = 
                            (typeof response.data === 'object' && response.data !== null && 'message' in response.data
                                ? String((response.data as { message: unknown }).message)
                                : `API request failed with status ${response.status}`);
                    } else if (response.status === 'FETCH_ERROR') {
                        errorMessage = response.error || 'Network error occurred';
                    } else if (response.status === 'PARSING_ERROR') {
                        status = response.originalStatus;
                        errorMessage = response.error || 'Failed to parse response';
                    }
                }
                
                return {
                    status,
                    data,
                    error: errorMessage,
                };
            },
        }),
    }),
})

const {
    useCallExternalAgentMutation
} = externalAgentApiSlice

export const externalAgentApiActions = {
    useCallExternalAgentMutation
}

